import { useEffect, useState, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { WebSocketProvider } from './contexts/WebSocketContext'
import ErrorBoundary from './components/ErrorBoundary'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import QueuePage from './pages/QueuePage'
import UsersPage from './pages/UsersPage'
import SettingsPage from './pages/SettingsPage'
import GroupsPage from './pages/GroupsPage'
import CustomersPage from './pages/CustomersPage'
import CustomerDetailPage from './pages/CustomerDetailPage'
import DuplicateCustomersPage from './pages/DuplicateCustomersPage'
import CallHistoryPage from './pages/CallHistoryPage'
import CallHistoryDetailPage from './pages/CallHistoryDetailPage'
import StatisticsPage from './pages/StatisticsPage'
import LineChatPage from './pages/LineChatPage'
import Layout from './components/Layout'
import WebCallPage from './pages/WebCallPage'
import CognitoSetupError from './components/CognitoSetupError'

// Cognito設定チェック
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID

function PrivateRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function App() {
  const initializeAuth = useAuthStore((state) => state.initialize)
  const initialized = useAuthStore((state) => state.initialized)
  const [authReady, setAuthReady] = useState(false)

  // 公開ページ判定（認証・WebSocket不要）
  const isPublicPage = window.location.pathname.startsWith('/c/')

  useEffect(() => {
    if (!isPublicPage) {
      initializeAuth()
    }
  }, [initializeAuth, isPublicPage])

  // 認証の初期化完了を待つ（10秒でタイムアウト → ログイン画面へ遷移）
  useEffect(() => {
    if (initialized || isPublicPage) {
      setAuthReady(true)
      return
    }

    const timeout = setTimeout(() => {
      console.warn('認証初期化がタイムアウトしました')
      setAuthReady(true)
    }, 10_000)

    return () => clearTimeout(timeout)
  }, [initialized, isPublicPage])

  // 公開ページ: 認証・WebSocket不要で即座にレンダリング
  if (isPublicPage) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/c/:linkId" element={<WebCallPage />} />
        </Routes>
      </ErrorBoundary>
    )
  }

  // Cognito設定が不足している場合はエラー画面を表示
  if (!userPoolId || !clientId) {
    return <CognitoSetupError />
  }

  // 認証初期化が完了するまで待機（WebSocket接続のタイミング問題を回避）
  if (!authReady) {
    return null
  }

  return (
    <ErrorBoundary>
      <WebSocketProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="queue" element={<QueuePage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/duplicates" element={<DuplicateCustomersPage />} />
            <Route path="customers/:customerId" element={<CustomerDetailPage />} />
            <Route path="call-history" element={<CallHistoryPage />} />
            <Route path="call-history/:callHistoryId" element={<CallHistoryDetailPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="line-chat" element={<LineChatPage />} />
            {/* 旧URL互換: 設定ハブへリダイレクト */}
            <Route path="line-settings" element={<Navigate to="/settings/line-ops" replace />} />
            <Route path="blocklist" element={<Navigate to="/settings/blocklist" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/:tab" element={<SettingsPage />} />
          </Route>
        </Routes>
      </WebSocketProvider>
    </ErrorBoundary>
  )
}

export default App
